import React, {useEffect, useState} from 'react'
import {Alert, Box, Button, CircularProgress, Stack, TextField, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import {RootState} from '../../store'
import OnboardingApi from '../../Api/onboarding'

type Props = {onSuccess: () => void}

export default function OnboardingStepGeneral({onSuccess}: Props) {
  const {t, i18n} = useTranslation()
  const store = useSelector((state: RootState) => state.vender)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const lang = i18n.language?.startsWith('ar') ? 'ar' : store.defLang || 'en'

  const [form, setForm] = useState({
    title: '',
    description: '',
    email: '',
    mobile: '',
    phone_code: '',
    currency_label: '',
    is_show_store_title: 1 as 0 | 1,
  })

  useEffect(() => {
    OnboardingApi.getGeneral((res: any) => {
      const data = res?.data || res || {}
      const titleObj = data.title || {}
      const descObj = data.description || {}
      setForm({
        title: titleObj[lang] || titleObj.en || Object.values(titleObj)[0] || '',
        description: descObj[lang] || descObj.en || Object.values(descObj)[0] || '',
        email: data.email || store.user?.email || '',
        mobile: data.mobile || store.user?.mobile || '',
        phone_code: data.phone_code || '',
        currency_label: data.currency || data.currency_code || String(data.currency_id || ''),
        is_show_store_title: Number(data.is_show_store_title ?? 1) as 0 | 1,
      })
    }, setIsLoading)
  }, [lang, store.user])

  const submit = () => {
    setError('')
    if (!form.title.trim() || !form.description.trim() || !form.email.trim() || !form.mobile.trim()) {
      setError(t('onboarding.general_required') as string)
      return
    }

    const langs = store.storeLangs?.length ? store.storeLangs : [lang]
    const title: Record<string, string> = {}
    const description: Record<string, string> = {}
    langs.forEach((code: string) => {
      title[code] = form.title
      description[code] = form.description
    })

    OnboardingApi.saveGeneral(
      {
        title,
        description,
        email: form.email,
        mobile: form.mobile,
        phone_code: form.phone_code || undefined,
        is_show_store_title: form.is_show_store_title,
      },
      () => onSuccess(),
      setIsSaving
    )
  }

  if (isLoading) {
    return (
      <Stack alignItems='center' py={6}>
        <CircularProgress />
      </Stack>
    )
  }

  return (
    <Stack gap={2}>
      <Typography fontWeight={600} fontSize={18}>
        {t('onboarding.step1_title') as string}
      </Typography>
      <Typography fontSize={14} color='text.secondary'>
        {t('onboarding.step1_subtitle') as string}
      </Typography>
      {error ? <Alert severity='warning'>{error}</Alert> : null}
      <TextField
        label={t('onboarding.site_title') as string}
        value={form.title}
        onChange={(e) => setForm({...form, title: e.target.value})}
        fullWidth
        required
      />
      <TextField
        label={t('onboarding.description') as string}
        value={form.description}
        onChange={(e) => setForm({...form, description: e.target.value})}
        fullWidth
        required
        multiline
        minRows={2}
      />
      <TextField
        label={t('onboarding.email') as string}
        value={form.email}
        onChange={(e) => setForm({...form, email: e.target.value})}
        fullWidth
        required
        type='email'
      />
      <TextField
        label={t('onboarding.mobile') as string}
        value={form.mobile}
        onChange={(e) => setForm({...form, mobile: e.target.value})}
        fullWidth
        required
      />
      <TextField
        label={t('onboarding.currency') as string}
        value={form.currency_label || '—'}
        fullWidth
        disabled
        helperText={t('onboarding.currency_hint') as string}
      />
      <Box display='flex' justifyContent='flex-end'>
        <Button variant='contained' disabled={isSaving} onClick={submit}>
          {isSaving ? <CircularProgress size={20} color='inherit' /> : (t('onboarding.continue') as string)}
        </Button>
      </Box>
    </Stack>
  )
}
